import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AuthContext } from 'context';
import AlertComponent from 'components/design-system/Alert/Alert';
import AutocompleteComponent from 'components/design-system/Autocomplete/Autocomplete';
import Heading from 'components/design-system/Heading/Heading';
import InputComponent from 'components/design-system/input/Input';
import LabelComponent from 'components/design-system/Label/Label';
import { InputAsyncController } from 'components/design-system/InputAsync/InputAsyncController';
import { PERMISOS } from 'const/Permisos';
import { DICTONARY } from 'const/Dictonary';
import roleServices from 'services/roles';
import serviceEmpresas from 'services/empresas';
import { CargaActividades, CargaRubros } from 'views/Business/hooks';
import { verificaEmpresaRegistradaNombre } from '../helpers/verificaCorreoRegistrado';
import adminService from 'services/admin';
import CheckboxComponent from 'components/design-system/Checkbox/Checkbox';
import { AlertaWarning } from 'utils/Alertas';

export const BusinessInfo = ({
  values,
  errors,
  touched,
  onChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  setFieldError,
  isSubmitting
}) => {
  const { isTienePermisos } = useContext(AuthContext);
  const { rubros, loadRubros, isLoadingRubros } = CargaRubros();
  const { actividades, loadActividades, isLoadingActividades } =
    CargaActividades();
  const [cargandoServicioSII, setCargandoServicioSII] = useState(false);
  const [roles, setRoles] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [isLoadingPerfiles, setIsLoadingPerfiles] = useState(true);

  useEffect(() => {
    loadRubros();
  }, []);

  // Llama servicio datos empresa SII
  const cargaDatosEmpresasSII = async rutEmpresa => {
    if (values.tipoNegocio === 'persona-natural') return;
    setCargandoServicioSII(true);

    setFieldValue('razonSocial', null);
    setFieldValue('nombreEmpresa', null);
    setFieldValue('rubro', {});
    setFieldValue('actividad', {});
    try {
      const response = await serviceEmpresas.datosEmpresasSII(
        { rutEmpresa: rutEmpresa.value },
        DICTONARY.INTERCEPTOR.DESACTIVADO
      );
      const data = response.data;
      setFieldValue('razonSocial', data.razonSocial);
      setFieldValue('nombreEmpresa', data.razonSocial);
      loadRubros();
      loadActividades(values.rubro.codigoCiuRubro);
    } catch (error) {
      console.error('Error al consultar servicio SII:', error);
      AlertaWarning(
        'No se encuentran disponibles los datos del SII para el rut ingresado.'
      );
      setFieldValue('razonSocial', null);
      setFieldValue('nombreEmpresa', null);
      setFieldValue('rubro', {});
      setFieldValue('actividad', {});
    } finally {
      setCargandoServicioSII(false);
    }
  };

  const obtenerTodasEmpresasAT = async ({
    setLoadingFetch,
    value,
    oldValue
  }) => {
    try {
      setLoadingFetch(true);
      setFieldTouched('nombreEmpresa');
      if (oldValue === value) {
        return;
      }

      const nombreExiste = await verificaEmpresaRegistradaNombre(value);
      if (nombreExiste) {
        setFieldError(
          'nombreEmpresa',
          'El nombre ingresado ya ha sido registrado'
        );
      } else {
        setFieldValue('nombreEmpresa', value);
      }
    } catch (error) {
      return error;
    } finally {
      setLoadingFetch(false);
    }
  };

  useEffect(() => {
    if (values.tipoNegocio === 'persona-natural') return;
    if (
      values.actividad &&
      values.rubro &&
      Object.keys(values.rubro).length === 0 &&
      Object.keys(values.actividad).length === 0
    ) {
      setFieldTouched('actividad');
      setFieldTouched('rubro');
      setFieldValue('rubro', '');
      setFieldValue('actividad', '');
    }
  }, [
    setFieldError,
    setFieldTouched,
    setFieldValue,
    values.actividad,
    values.rubro,
    values.tipoNegocio
  ]);

  // LOGICA para obtener los roles de la empresa

  const cargaRoles = useCallback(async () => {
    // Validar permisos
    if (!isTienePermisos(PERMISOS.actualizarRolNegocio)) return;
    try {
      setIsLoadingRoles(true);
      const response = await roleServices.roles(
        {
          ordenarPor: 'nombreRol',
          totalPagina: DICTONARY.PAGINACION.ALL_DATA
        },
        DICTONARY.INTERCEPTOR.DESACTIVADO
      );
      setRoles(response.data.content);
    } catch (error) {
      console.log('ERROR al cargar roles ', error);
    } finally {
      setIsLoadingRoles(false);
    }
  }, [isTienePermisos]);

  useEffect(() => {
    cargaRoles();
  }, []);

  // LOGICA para obtener los perfiles de la empresa segun el rol seleccionado
  const cargaPerfiles = useCallback(async () => {
    if (!isTienePermisos(PERMISOS.actualizarRolNegocio)) return;

    if (values.rol && values.rol.length <= 0) {
      return;
    }

    // Logica para obtener el codigo de perfil segun el rol seleccionado
    const codigosPerfiles = Object.entries(DICTONARY.VALIDACION_ROLES).map(
      ([key, value]) => ({
        nombre: value.NOMBRE,
        codigo_perfil: value.CODIGO_PERFIL,
        codigo_rol: value.CODIGO
      })
    );

    try {
      setIsLoadingPerfiles(true);

      const requestPerfiles = [];
      for (let index = 0; index < values.rol.length; index++) {
        const rol = values.rol[index];
        requestPerfiles.push(
          new Promise((resolve, reject) => {
            adminService
              .getPerfil(
                codigosPerfiles.find(
                  perfil => perfil.codigo_rol === rol.codigoRol
                ).codigo_perfil,
                null,
                DICTONARY.INTERCEPTOR.DESACTIVADO
              )
              .then(response => {
                resolve(response.data.permisos);
              })
              .catch(error => {
                reject(error);
              });
          })
        );
      }

      Promise.all(requestPerfiles)
        .then(response => {
          let permisosEstructurados = {};
          for (const roles of response) {
            for (const item of roles) {
              permisosEstructurados[item.codigoPermiso] = {
                codigoPermiso: item.codigoPermiso,
                nombrePermiso: item.nombrePermiso,
                descripcionPermiso: item.descripcionPermiso
              };
            }
          }
          setFieldValue('permisosRolSeleccionado', permisosEstructurados);
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          setIsLoadingPerfiles(false);
        });
    } catch (error) {
      console.log('ERROR al cargar perfiles ', error);
    } finally {
      setIsLoadingPerfiles(false);
    }
  }, [values.rol]);

  useEffect(() => {
    cargaPerfiles();
  }, [values.rol]);

  const categoriaSeleccionada =
    values.rol.length > 0 ? values.rol[0].categoria : null;

  const handleChange = (event, newValue) => {
    if (newValue.length === 0) {
      setFieldValue('rol', []);
    } else {
      const categoriaActual = newValue[0].categoria;
      const nuevosRoles = newValue.filter(
        role => role.categoria === categoriaActual
      );
      setFieldValue('rol', nuevosRoles);
    }
  };

  return (
    <div>
      <Heading type="h3">Información del negocio</Heading>

      <div className="grid grid-cols-12 gap-4 pb-6">
        <div className="col-span-6">
          <InputAsyncController
            name="nombreEmpresa"
            label={<LabelComponent>Nombre Empresa</LabelComponent>}
            touched={touched.nombreEmpresa ? true : false}
            fullWidth
            setFieldValue={data => setFieldValue('nombreEmpresa', data)}
            setFieldTouched={() => setFieldTouched('nombreEmpresa')}
            value={values.nombreEmpresa ? values.nombreEmpresa : ''}
            isSubmitting={isSubmitting}
            onBlur={() => setFieldTouched('nombreEmpresa')}
            onChange={event => {
              setFieldValue('nombreEmpresa', event.target.value);
              setFieldTouched('nombreEmpresa');
            }}
            fetchCall={controllerValues =>
              obtenerTodasEmpresasAT({
                setLoadingFetch: controllerValues.setIsLoading,
                value: controllerValues.inputValue,
                oldValue: values.nombreEmpresaOriginal
              })
            }
            estado={
              errors.nombreEmpresa
                ? { tipo: 'error', mensaje: errors.nombreEmpresa }
                : null
            }
          />
        </div>

        {isTienePermisos(PERMISOS.actualizarRolNegocio) && (
          <div className="col-span-12 md:col-span-6">
            <AutocompleteComponent
              id="rol"
              name="rol"
              accesor={'nombreRol'}
              multiple
              options={DICTONARY.ROLES_POR_CATEGORIA || []}
              getOptionLabel={option => option.nombreRol || ''}
              groupBy={option => option.categoria}
              disableCloseOnSelect
              onChange={handleChange}
              value={values.rol}
              getOptionSelected={(option, value) =>
                option.codigoRol === value.codigoRol
              }
              renderOption={(props, option, { selected }) => {
                const disabled =
                  categoriaSeleccionada &&
                  categoriaSeleccionada !== option.categoria;
                return (
                  <li
                    {...props}
                    key={option.codigoRol}
                    style={{
                      pointerEvents: disabled ? 'none' : 'auto',
                      opacity: disabled ? 0.5 : 1
                    }}
                  >
                    <CheckboxComponent checked={selected} disabled={disabled} />
                    {option.nombreRol}
                  </li>
                );
              }}
            >
              <InputComponent
                touched={touched.rol}
                fullWidth
                autoComplete={'off'}
                label={<LabelComponent>Rol</LabelComponent>}
                estado={
                  errors.rol ? { tipo: 'error', mensaje: errors.rol } : null
                }
              />
            </AutocompleteComponent>
          </div>
        )}

        {values.tipoNegocio === 'empresa' && (
          <>
            <div className="col-span-12 md:col-span-6">
              <InputAsyncController
                name="rutEmpresa"
                touched={touched.nombre ? true : false}
                fullWidth
                setFieldValue={data => setFieldValue('rutEmpresa', data)}
                setFieldTouched={() => setFieldTouched('rutEmpresa')}
                value={values.rutEmpresa}
                isSubmitting={isSubmitting}
                onBlur={() => setFieldTouched('nombre')}
                onChange={event => {
                  setFieldValue('rutEmpresa', event.target.value);
                  setFieldTouched('nombre');
                }}
                label={<LabelComponent>Rut Empresa</LabelComponent>}
                fetchCall={controllerValues =>
                  cargaDatosEmpresasSII({
                    setLoadingFetch: controllerValues.setIsLoading,
                    value: controllerValues.inputValue
                  })
                }
                estado={
                  errors.nombre
                    ? { tipo: 'error', mensaje: errors.nombre }
                    : null
                }
              />
            </div>
            <div className="col-span-12">
              <AlertComponent>
                <strong>La razón social, rubro y actividad</strong> han sido{' '}
                <strong>obtenidos de forma automática</strong> con la
                información entregada por Servicio de Impuestos Internos (SII),
                <strong>
                  {' '}
                  según su última actualización de noviembre 2022 con datos
                  registrados hasta el 2021.
                </strong>{' '}
                <br />
                En el caso de que estos datos automáticos no corresponden con la
                situación actual de tu empresa, por favor registra tu negocio y
                posterior a esto solicita mediante el botón de Ayuda la
                actualización adjuntando los datos del SII correctos.
              </AlertComponent>
            </div>
            <div className="col-span-12">
              <InputComponent
                autoComplete={'off'}
                fullWidth
                label={<LabelComponent>Razón social</LabelComponent>}
                name="razonSocial"
                onBlur={e => {
                  setFieldValue('nombreModificado', false);
                  setFieldTouched('razonSocial');
                  setFieldTouched('nombreEmpresa');
                }}
                touched={touched.razonSocial}
                onChange={e => {
                  setFieldValue('nombreModificado', false);
                  onChange(e);
                }}
                value={values.razonSocial ? values.razonSocial : ''}
                estado={
                  errors.razonSocial
                    ? {
                        tipo: 'error',
                        mensaje: errors.razonSocial
                      }
                    : null
                }
              />
            </div>
            <div className="col-span-12 lg:col-span-6">
              <AutocompleteComponent
                options={rubros}
                id="rubro"
                name="rubro"
                loading={isLoadingRubros}
                accesor={'nombreRubro'}
                onChange={(ev, selected, reason) => {
                  const data = { name: 'rubro', value: selected };
                  if (reason === 'clear') {
                    data.value = '';
                  }
                  if (data.value) {
                    setFieldValue('nombreModificado', false);
                    setFieldValue('actividad', null);
                    setFieldValue('rubro', data.value);
                    setFieldTouched('actividad');

                    data.value.codigoCiuRubro && loadRubros();
                    data.value.codigoCiuRubro &&
                      loadActividades(data.value.codigoCiuRubro);
                  } else {
                    setFieldValue('rubro', '');
                    setFieldValue('actividad', '');
                  }
                }}
                onBlur={e => {
                  setFieldValue('nombreModificado', false);
                  setFieldTouched('rubro');
                }}
                getOptionLabel={option => option.nombreRubro || ''}
                value={values.rubro}
              >
                <InputComponent
                  autoComplete={'off'}
                  touched={touched.rubro}
                  disabled={
                    errors.rutEmpresa || cargandoServicioSII ? true : false
                  }
                  fullWidth
                  label={<LabelComponent>Rubro</LabelComponent>}
                  estado={
                    errors.rubro
                      ? {
                          tipo: 'error',
                          mensaje: errors.rubro
                        }
                      : null
                  }
                />
              </AutocompleteComponent>
            </div>

            <div className="col-span-12 lg:col-span-6">
              <AutocompleteComponent
                id="actividades"
                loading={isLoadingActividades}
                name="actividad"
                accesor={'nombreActividad'}
                options={actividades}
                onChange={(ev, selected, reason) => {
                  const data = { name: 'actividad', value: selected };
                  if (reason === 'clear') {
                    data.value = '';
                  }
                  if (data.value) {
                    setFieldValue('nombreModificado', false);
                    setFieldValue('actividad', data.value);
                  }
                }}
                onBlur={e => {
                  setFieldValue('nombreModificado', false);
                  setFieldTouched('actividad');
                }}
                getOptionLabel={option => option.nombreActividad || ''}
                value={values.actividad}
              >
                <InputComponent
                  autoComplete={'off'}
                  disabled={
                    errors.rutEmpresa ||
                    errors.razonSocial ||
                    errors.nombreEmpresa
                      ? true
                      : false
                  }
                  fullWidth
                  label={<LabelComponent>Actividad</LabelComponent>}
                  touched={touched.actividad}
                  estado={
                    errors.actividad
                      ? {
                          tipo: 'error',
                          mensaje: errors.actividad
                        }
                      : null
                  }
                />
              </AutocompleteComponent>
            </div>
          </>
        )}

        {values.permisosRolSeleccionado[PERMISOS.areaDeTrabajo] &&
          values.areaTrabajo !== null && (
            <div className="col-span-12 md:col-span-6">
              <InputComponent
                fullWidth
                label={<LabelComponent>Zona o área de trabajo</LabelComponent>}
                name="areaTrabajo"
                onChange={onChange}
                onBlur={handleBlur}
                value={values.areaTrabajo}
                touched={touched.areaTrabajo}
                estado={
                  errors.areaTrabajo
                    ? { tipo: 'error', mensaje: errors.areaTrabajo }
                    : null
                }
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default BusinessInfo;
